import { DEFAULT_LOCALE_CODE } from '../const';
import { CourseUtils } from './CourseUtils';
import { CurriculumUtils } from './CurriculumUtils';

/**
 * A utility class useful for helping to determine things about a particular
 * category or categories in general. Use of this class is entirely optional.
 */
export class CategoryUtils {

    /**
     * Retrieves the ID from the provided category data.
     * 
     * @param { object } categoryData - The data to read.
     * @returns { string }
     */
    static getId( categoryData ) {
        return categoryData?.node?.id;
    }

    /**
     * Retrieves the array of locale data from the provided category data. This
     * locale data contains translations related to the category like the name and
     * description.
     * 
     * @param { object } categoryData - The data to read.
     * @returns { { locale: string, name: string, description: string } }
     */
    static getLocales( categoryData ) {
        if ( categoryData?.node?.locales ) {
            return categoryData.node.locales?.map( ( localeData ) => ( {
                locale: localeData?.code, // TODO: Rename prop to `code` eventually?
                name: localeData?.name,
                description: localeData?.description,
            } ) );
        }
        else {
            return undefined;
        }
    }

    /**
     * Retrieves the name from a category object in the language represented by the
     * provided locale code. If no locale code is provided, a default locale code
     * of `en-US` will be used.
     * 
     * @param { object } categoryData - The data to read.
     * @param { object } options - Options to change the retrieval method.
     * @returns { string }
     */
    static getName( categoryData, { localeCode = DEFAULT_LOCALE_CODE, allowFallback = true } ) {

        const locales = this.getLocales( categoryData );

        return locales?.find( l => l.locale == localeCode )?.name ??
            locales?.find( l => l.locale == DEFAULT_LOCALE_CODE )?.name ??
            locales?.[ 0 ] ?? 'Unknown';

    }

    /**
     * Retrieves the description from a category object in the language represented
     * by the provided locale code. If no locale code is provided, a default locale
     * code of `en-US` will be used.
     * 
     * @param { object } categoryData - The data to read.
     * @param { object } options - Options to change the retrieval method.
     * @returns { string }
     */
    static getDescription( categoryData, { localeCode = DEFAULT_LOCALE_CODE, allowFallback = true } ) {
        // Validate the locale code parameter.
        if ( !localeCode ) {
            localeCode = DEFAULT_LOCALE_CODE;
        }

        const locales = this.getLocales( categoryData );
        let translatedDescription = locales?.find( ( data ) => ( data?.locale == localeCode ) )?.description;

        // If no translated name is found and fallbacks are allowed, attempt to find a
        // translated name in the site's default language.
        if ( !translatedDescription && allowFallback && localeCode != DEFAULT_LOCALE_CODE ) {
            translatedDescription = this.getDescription( categoryData, { localeCode: DEFAULT_LOCALE_CODE, allowFallback: false } );
        }

        return translatedDescription;
    }

    /**
     * Retrieves the sort order from the provided category data.
     * 
     * @param { object } categoryData - The data to read.
     * @returns { number | null }
     */
    static getSortOrder( categoryData ) {
        return categoryData?.node?.sortOrder;
    }

    /**
     * Sorts the provided list of categories by their sort order value in ascending
     * order.
     * 
     * @param { object[] } categoryDataList - The data to read.
     * @returns { object[] }
     */
    static sortCategoriesBySortOrder( categoryDataList, descending = false ) {
        return ( categoryDataList || [] )?.sort( ( a, b ) => {
            const orderA = this.getSortOrder( a ) || 0;
            const orderB = this.getSortOrder( b ) || 0;
            return ( !descending ) ? ( orderA - orderB ) : ( orderB - orderA );
        } );
    }

    /**
     * Retrieves the segment ID of the brand the category is attached to. This segment
     * ID is the ID of the Roster group being used as a segmentation group.
     * 
     * @param { object } categoryData - The data to read.
     * @returns { number | null }
     */
    static getBrandSegmentId( categoryData ) {
        return categoryData?.node?.brand?.segmentId;
    }

    /**
     * Returns only the courses that are connected to the provided category data.
     * 
     * @param { object } categoryData - The category data to read.
     * @param { object[] } courseDataList - The course list to look through for connections.
     * @returns { object[] }
     */
    static getConnectedCourses( categoryData, courseDataList ) {
        const categoryId = this.getId( categoryData );
        return ( courseDataList || [] ).filter( ( courseData ) => {
            return ( CourseUtils.getCategoryIds( courseData ) || [] )?.includes( categoryId ) || false;
        } );
    }

    /**
     * Returns only the curriculums that are connected to the provided category data.
     * 
     * @param { object } categoryData  - The category data to read.
     * @param { object[] } curriculumDataList - The curriculum list to look through for connections.
     * @returns { object[] }
     */
    static getConnectedCurriculums( categoryData, curriculumDataList ) {
        const categoryId = this.getId( categoryData );
        return ( curriculumDataList || [] ).filter( ( courseData ) => {
            return ( CurriculumUtils.getCategoryIds( courseData ) || [] )?.includes( categoryId ) || false;
        } );
    }

}
